import axios from '../helpers/axios';
import { logError } from '../services';
import qs from 'querystring';
import {
  IAllowedUsers,
  ICreateOrder,
  IDescription,
  IManagePlaceOrderReportsTo,
  IManagePlaceOrderRole,
  IManagePlaceOrderUpdateUser,
  IManagePlaceOrderUser,
  IOrderAudit,
  IOrderDescriptionUpdateOrUpdate,
  IOrderDetail,
  IOrderDetailRes,
  IOrderDetailUpdate,
  IOrderHistoryRes,
  IOrderOwner,
  IOrderStatus,
  IOrderType,
  IOrderTypeCreate,
  IOrderTypeUpdate,
  IResponse,
  IFavoriteOrder
} from '../models';
import { customStringify } from '../helpers';

export const getOrders = async (filters?: {
  orderSearch?: number | string;
  neighborhoodId?: number | string;
  vendorId?: number | string;
  lotNumber?: number | string;
  orderPlacer?: string;
  descriptionId?: string;
  orderType?: number | string;
  changeOrder?: boolean;
  active?: boolean;
  status?: 'New' | 'Approved' | 'Remediating' | 'Placed' | 'Delivered' | 'Cancelled' | string;
  sortBy?:
    | 'Id'
    | 'Date'
    | 'Neighborhood'
    | 'LotNumber'
    | 'OrderPlacer'
    | 'Status'
    | 'FavoriteName'
    | string;
  sortDirection?: 'Asc' | 'Desc' | string;
  page?: number;
  perPage?: number;
  isFavorite?: boolean;
}): Promise<IOrderDetailRes> => {
  try {
    const { data } = await axios.get('/api/placeorder', {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getOrders failed');
    return Promise.reject(error);
  }
};

export const getOrderDetail = async (
  fieldOrderId: number | string,
  partsHistory?: boolean
): Promise<IOrderDetail> => {
  try {
    const { data } = await axios.get(
      `/api/placeorder/${fieldOrderId}${partsHistory ? `?partsHistory=${partsHistory}` : ''}`
    );
    return data;
  } catch (error) {
    logError(error, 'getOrderDetail failed');
    return Promise.reject(error);
  }
};

export const updateOrderDetail = async (order: IOrderDetailUpdate): Promise<any> => {
  try {
    const { data } = await axios.put('/api/placeorder', order);
    return data;
  } catch (error) {
    logError({ error, payload: order }, 'updateOrderDetail failed');
    return error.response.data;
  }
};

export const updateOrderStatus = async (status: {
  fieldOrderId: number;
  status: string;
  comment: string;
}): Promise<any> => {
  try {
    const { data } = await axios.post('/api/placeorder/status', status);
    return data;
  } catch (error) {
    logError({ error, payload: { status } }, 'updateOrderStatus failed');
    return error.response.data;
  }
};

export const updateOrderVendor = async (vendorData: {
  fieldOrderPartId: number;
  vendorId: number;
  pricePerUnit: number;
}): Promise<any> => {
  try {
    const { data } = await axios.put('/api/placeorder/partvendor', vendorData);
    return data;
  } catch (error) {
    logError({ error, payload: { vendorData } }, 'updateOrderVendor failed');
    return Promise.reject(error);
  }
};

export const getPlaceOrderStatuses = async (): Promise<IOrderStatus[]> => {
  try {
    const { data } = await axios.get('/api/placeorder/statuses');
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderStatuses failed');
    return Promise.reject(error);
  }
};

export const getPlaceOrderOwners = async (): Promise<IOrderOwner[]> => {
  try {
    const { data } = await axios.get('/api/placeorder/owners');
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderOwners failed');
    return Promise.reject(error);
  }
};

export const getPlaceOrderRoles = async (): Promise<IResponse<IManagePlaceOrderRole[]>> => {
  try {
    const { data } = await axios.get('/api/place-order/roles');
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderRoles failed');
    return Promise.reject(error);
  }
};

export const getPlaceOrderHistory = async (filters: {
  neighborhoodId?: string | number;
  lotNumber?: string | number | null;
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  sortDirection?: string;
  orderType?: string;
}): Promise<IOrderHistoryRes> => {
  try {
    const { data } = await axios.get(`/api/placeorder/history`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderHistory failed');
    return Promise.reject(error);
  }
};

export const getPlaceOrderTypes = async (filters?: {
  id?: string;
  name?: string;
  ids?: string[];
  isActive?: boolean;
}): Promise<IOrderType[]> => {
  try {
    const { data } = await axios.get(`/api/placeorder/types`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderTypes failed');
    return Promise.reject(error);
  }
};

export const createOrderType = async (order: IOrderTypeCreate): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/placeorder/types`, order, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError({ error, payload: order }, 'createOrderType failed');
    return error.response.data;
  }
};

export const updateOrderType = async (id: number, order: IOrderTypeUpdate): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/placeorder/types/${id}`, order, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError({ error, payload: { id, order } }, 'updateOrderType failed');
    return error.response.data;
  }
};

export const createOrder = async (order: ICreateOrder): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/placeorder`, order, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError({ error, payload: order }, 'createOrder failed');
    return error.response.data;
  }
};

export const getPlaceOrderDuplicate = async (filters: {
  neighborhoodId?: string;
  lotNumber?: string;
  partId?: number;
}): Promise<boolean> => {
  try {
    const { data } = await axios.get('/api/placeorder/duplicate', {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderDuplicate failed');
    return Promise.reject(error);
  }
};

export const getPlaceOrderDescriptions = async (filters?: {
  id?: string;
  name?: string;
  ids?: string[];
  isActive?: boolean;
}): Promise<IDescription[]> => {
  try {
    const { data } = await axios.get(`/api/placeorder/descriptions`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderDescriptions failed');
    return Promise.reject(error);
  }
};

export const createOrderDescription = async (
  description: IOrderDescriptionUpdateOrUpdate
): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/placeorder/descriptions`, description, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError({ error, payload: description }, 'createOrderDescription failed');
    return error.response.data;
  }
};

export const updateOrderDescription = async (
  id: number,
  description: IOrderDescriptionUpdateOrUpdate
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/placeorder/descriptions/${id}`, description, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError({ error, payload: description }, 'updateOrderDescription failed');
    return Promise.reject(error);
  }
};

export const getPlaceOrderUsers = async (filters?: {
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDirection?: string;
  query?: string;
  name?: string;
  roleId?: string | number;
  reportsToAccountName?: string | number;
  accountNames?: string[];
}): Promise<IResponse<IManagePlaceOrderUser[]>> => {
  try {
    const { data } = await axios.get('/api/place-order/users', {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderUsers failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPlaceOrderUser = async (accountName: string): Promise<IManagePlaceOrderUser> => {
  try {
    const { data } = await axios.get(`/api/place-order/users/${accountName}`);
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderUser failed');
    return Promise.reject(error);
  }
};

export const getPlaceOrderAvailableReportsTo = async (
  accountName: string
): Promise<IManagePlaceOrderReportsTo[]> => {
  try {
    const { data } = await axios.get(`/api/place-order/users/${accountName}/available-reports-to`);
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderAvailableReportsTo failed');
    return Promise.reject(error);
  }
};

export const updatePlaceOrderUser = async (
  accountName: string,
  user: IManagePlaceOrderUpdateUser
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/place-order/users/${accountName}`, user);
    return data;
  } catch (error) {
    logError({ error, payload: { accountName, user } }, 'updatePlaceOrderUser failed');
    return Promise.reject(error.response.data);
  }
};

export const createPlaceOrderUser = async (
  accountName: string,
  user: IManagePlaceOrderUpdateUser
): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/place-order/users/${accountName}`, user);
    return data;
  } catch (error) {
    logError({ error, payload: { accountName, user } }, 'createPlaceOrderUser failed');
    return Promise.reject(error.response.data);
  }
};

export const deletePlaceOrderUser = async (accountName: string): Promise<void> => {
  try {
    const { data } = await axios.delete(`/api/place-order/users/${accountName}`);
    return data;
  } catch (error) {
    logError(error, 'deletePlaceOrderUser failed');
    return Promise.reject(error.response.data);
  }
};

export const getAllReportsTo = async (): Promise<IManagePlaceOrderReportsTo[]> => {
  try {
    const { data } = await axios.get(`/api/place-order/users/all-reports-to`);
    return data;
  } catch (error) {
    logError(error, 'getAllReportsTo failed');
    return Promise.reject(error);
  }
};
export const getAllowedUsers = async (): Promise<IAllowedUsers[]> => {
  try {
    const { data } = await axios.get(`/api/place-order/users/allowed-users`);
    return data;
  } catch (error) {
    logError(error, 'getAllowedUsers failed');
    return Promise.reject(error);
  }
};

export const enableAllowedUsers = async (allowedUsers: string[]): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/place-order/users/allowed-users`, {
      allowedAccounts: allowedUsers
    });
    return data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          allowedUsers
        }
      },
      'enableAllowedUsers failed'
    );
    return Promise.reject(error);
  }
};

export const getOrderAudit = async (
  orderId: number | string
): Promise<IResponse<IOrderAudit[]>> => {
  try {
    const { data } = await axios.get(`/api/audits/orders/${orderId}`);
    return data;
  } catch (error) {
    logError(error, 'getOrderAudit failed');
    return Promise.reject(error);
  }
};

export const createOrderFavorite = async (
  orderId: number | string,
  favoriteName: string
): Promise<number> => {
  try {
    const { data } = await axios.post(
      `/api/placeorder/favorites/${orderId}?orderName=${favoriteName}`
    );
    return data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          orderId,
          favoriteName
        }
      },
      'createOrderFavorite failed'
    );
    return Promise.reject(error);
  }
};

export const removeOrderFavorite = async (
  orderId: number | string,
  userName?: string
): Promise<number> => {
  try {
    const { data } = await axios.delete(
      `/api/placeorder/favorites/${orderId}${userName ? `?userName=${userName}` : ''}`
    );
    return data;
  } catch (error) {
    logError(error, 'removeOrderFavorite failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createOrderImages = async (imagePost: FormData): Promise<string[]> => {
  try {
    const { data } = await axios.post(`/api/placeorder/images`, imagePost);
    return data;
  } catch (error) {
    // since it is form data need to extract the values see in logs
    logError(
      {
        error,
        payload: {
          images: imagePost.getAll('images').map(i => customStringify(i))
        }
      },
      'createOrderImages failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const getOrderFavorites = async (filters?: {
  page?: number;
  perPage: number;
  sortBy?: string;
  sortDirection?: string;
  searchTerm?: string;
}): Promise<IResponse<IFavoriteOrder[]>> => {
  try {
    const { data } = await axios.get(`/api/placeorder/favorites`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getFavorites failed');
    return Promise.reject(error);
  }
};

export const getSingleOrderFavorite = async (favoriteId: number | string): Promise<number> => {
  try {
    const { data } = await axios.get(`/api/placeorder/favorites/${favoriteId}`);
    return data;
  } catch (error) {
    logError(error, 'getSingleFavorite failed');
    return Promise.reject(error);
  }
};

export const updateOrderFavorite = async (
  favoriteId: number | string,
  favoriteName: string
): Promise<number> => {
  try {
    const { data } = await axios.put(
      `/api/placeorder/favorites/${favoriteId}?orderName=${favoriteName}`
    );
    return data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          favoriteId,
          favoriteName
        }
      },
      'updateOrderFavorite failed'
    );
    return Promise.reject(error?.response?.data);
  }
};
